import * as React from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";

import { Button } from "src/library/components/Button";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal/BasicModal";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { Text } from "src/library/components/Text";
import { useSupportApi } from "src/modules/support/api/support.api";
import { WysiwygEditorRef } from "src/library/components/WysiwygEditor";
import RespondTextAreaContainer from "src/modules/support/components/RespondTextAreaContainer";
import { media } from "src/library/utils";

export interface SupportTicketInternalNoteModalProps extends Pick<BasicModalProps, "onDismiss" | "open"> {
  supportTicketId: number;
  resolved: boolean;
  onSuccess: () => void;
}

/**
 * Renders a modal that displays a note to user explaining what a private internal note is,
 * as well as the wysiwyg editor to write the internal note.
 */
export const SupportTicketInternalNoteModal: React.FC<SupportTicketInternalNoteModalProps> = ({
  onDismiss, open, supportTicketId, resolved, onSuccess
}) => {
  const [ updateSupportTicketRes, updateSupportTicketReq ] = useSupportApi("UPDATE_SUPPORT_TICKET");
  const [ newMessage, setNewMessage ] = React.useState<string>("");
  const newMessageRef = React.useRef<WysiwygEditorRef>(null);

  const handleSubmit = React.useCallback(() => {
    // check if content in message exists.
    // newMessage is set by wysiwyg. Length of 8 is equal to initial content provided by wysiwyg which returns <p></p>.
    if (newMessage.length === 8) {
      return;
    }

    updateSupportTicketReq({
      supportTicketId,
      internalMessage: newMessage,
      resolved
    });
  }, [
    newMessage,
    resolved,
    supportTicketId,
    updateSupportTicketReq
  ]);

  // call consumer onSuccess handler
  React.useEffect(() => {
    if (updateSupportTicketRes.data) {
      toast.success("Successfully added private internal note.");
      onSuccess();
    }
  }, [ onSuccess, updateSupportTicketRes.data ]);

  return (
    <StyledBasicModal
      title="New Internal Note"
      open={open}
      onDismiss={() => onDismiss()}
    >
      {updateSupportTicketRes.error && (
        <ErrorNotice
          message={updateSupportTicketRes.error.message}
          type="error"
        />
      )}

      <Text>
        This internal note will be added to this support ticket but will only be viewable by admin and not the customer.
        Internal notes are displayed in the chat message thread with a different background colour in order to make it clear to the admin the message is not viewable by the customer.
      </Text>

      {updateSupportTicketRes.pending ? (
        <LoadingSpinner type="circular" />
      ) : (
        <>
          <RespondTextAreaContainer
            innerRef={newMessageRef}
            label=""
            handleChange={(value => setNewMessage(value))}
          >
            <Button
              $buttonType="primary"
              onClick={() => handleSubmit()}
              loading={updateSupportTicketRes.pending}
              disabled={newMessage.length < 9}
            >
              Submit Private Note
            </Button>

          </RespondTextAreaContainer>
        </>
      )}
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  ${({ theme }) => css`
    max-height: calc(100vh - 40px);
    min-height: 400px;
    min-width: 700px;

    ${media({ lt: theme.breakpoints.sm })} {
      min-width: 100%;
    }

    .editor-class {
      background-color: #FAFAFA;
    }

    .button-wrapper button {
      margin-top: ${theme.spacing.margins.lg};
    }
  `}
`;